import { Faq } from 'libs/http/api/content/content.types';

import type { DataFlowChannel } from 'ui/organisms/DataFlowSection/DataFlowSection.types';

type Translate = (key: string) => string;

export const getBusinessCreditRiskChannels = (t: Translate): DataFlowChannel[] => [
  {
    title: t('Platform'),
    description: t('Search, reports and monitoring for analysts working cases directly.'),
    links: [{ label: t('Start searching'), href: '/regis' }],
  },
  {
    title: 'Regis',
    badge: { label: 'AI' },
    description: t(
      'Ask the whole dataset in plain language. Regis answers on registry data and returns the filing behind every number, so the answer can be checked rather than trusted.',
    ),
    sub: [
      {
        title: 'Regis AI',
        text: t(
          'The assistant. An analyst types a question and reads the answer, in the platform or connected to Claude and ChatGPT. No query language, no integration work.',
        ),
        link: { label: 'Regis AI', href: '/regis' },
      },
      {
        title: 'Regis API',
        text: t('The same query engine as one endpoint.'),
        code: 'POST /v1/regis/query',
        link: { label: 'Regis API', href: '/regis-api' },
      },
    ],
  },
  {
    title: 'API',
    badge: { label: 'MCP server', mono: true },
    description: t('Instant KYB, financials, ownership and credit endpoints, plus registry data inside any AI agent.'),
    links: [{ label: t('Get API keys'), href: '/book-a-demo' }],
  },
  {
    title: t('Data feeds'),
    description: t('Bulk datasets, enrichment and redistribution licensing to power your own product.'),
    links: [{ label: t('Talk to data sales'), href: '/book-a-demo' }],
  },
  {
    title: t('CRM integrations'),
    description: t('Enriched records and monitoring inside the systems your teams already work in.'),
    links: [
      { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
      { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
    ],
  },
  {
    title: t('AI connectors'),
    description: t('Registry data inside your AI assistant, under your own access controls.'),
    links: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: t('Add to ChatGPT'), href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
];

export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'score-calculated',
    default_opened: true,
    question: t('How is the score calculated?'),
    answer: t(
      '1 to 100 against a twelve month horizon, from more than 150 parameters on the filed record: accounts and the ratios derived from them, payment behaviour, registered security, judgments, ownership and group position, age, status and sector. Bands are 71–100 low, 41–70 moderate, 1–40 high. The score is a modelled field and is labelled as one.',
    ),
  },
  {
    id: 'limit-basis',
    question: t('What sits behind the recommended limit?'),
    answer: t(
      'Filed accounts, payment behaviour, security already registered against the assets, and position inside the group. It is a maximum supported exposure rather than an instruction. The inputs are shown so a committee can adjust for terms, concentration or its own appetite.',
    ),
  },
  {
    id: 'coverage',
    question: t('Which countries do you hold judgment and lien data for?'),
    answer: t(
      'Coverage differs by data class because the underlying registers do. UK and Irish judgments come from the statutory registers, US secured lending from state filing offices, registered charges from national company registries. Coverage per class is confirmed per jurisdiction at contract rather than claimed in aggregate.',
    ),
  },
  {
    id: 'judgment-duration',
    question: t('How long does a judgment stay on file?'),
    answer: t(
      'For England and Wales, six years from registration. Payment within one month can remove the entry. Payment after that marks it satisfied and it stays visible for the remaining term. Registration date, amount, court, case number and satisfaction status are held separately, so a satisfied judgment is never read as an open one.',
    ),
  },
  {
    id: 'lapsed-financing',
    question: t('Why does a lapsed financing statement matter?'),
    answer: t(
      'A UCC-1 is effective for five years from filing. It lapses automatically unless a UCC-3 continuation is filed inside the six months immediately before the lapse date, and a continuation filed outside that window has no effect. So a lien on the record can already be unperfected, and a live one can be days from expiring. We carry filing date, lapse date and window, and alert when the window opens.',
    ),
  },
  {
    id: 'payment-source',
    question: t('Where does payment behaviour data come from?'),
    answer: t(
      'A contributor panel submitting their own receivables ledgers. Contributor identity is confidential, and a days beyond terms figure publishes only once a minimum number of independent trade lines exist. Ageing buckets, line count and value on file are shown alongside it so the sample size is visible.',
    ),
  },
  {
    id: 'thin-file',
    question: t('What happens when a company has filed nothing useful?'),
    answer: t(
      'Small companies filing abridged or micro-entity accounts give a thin financial picture, and newly incorporated ones give none. The score falls back on what does exist: payment behaviour, registered security, judgments, director history and group position. Where a field is absent it is returned as absent rather than estimated, so a thin file is visible as a thin file.',
    ),
  },
  {
    id: 'matching',
    question: t('How do you match our records to yours?'),
    answer: t(
      'Send your book and we resolve it against registry identity, returning the registry number, our identifier and a match confidence per row. Unmatched and ambiguous rows come back flagged rather than force-matched. Once resolved, the mapping persists, so subsequent deltas land on the right record without rematching.',
    ),
  },
  {
    id: 'own-scorecard',
    question: t('Can we use the data in our own scorecard?'),
    answer: t(
      'Yes. Bulk and delta files are built for model development and licences cover use inside your own models and products. Records carry open registry identifiers as well as ours, so nothing you build is tied to our keys.',
    ),
  },
  {
    id: 'refresh-speed',
    question: t('How quickly does a change reach us?'),
    answer: t(
      'Registry, court and financing statement sources are tracked daily and the changed field is pushed on the day it is picked up. Scores and limits recalculate when a new filing lands. Payment behaviour updates monthly, on the contributor cycle.',
    ),
  },
];
